import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router';
import { FaArrowRight, FaCheck, FaInfinity } from 'react-icons/fa';
import { HiOutlineCloudDownload } from 'react-icons/hi';
import DownloadCTA from './DownloadCTA';
import DirectCheckoutLink from './DirectCheckoutLink';
import { OfferGuarantee, OfferPrice } from './OfferPrice';
import { canonicalPagePath, captureAttribution } from '../lib/attribution';
import { trackEvent } from '../lib/analytics';
import { commercialOffer, licenseRightsText } from '../lib/commercialOffer';
import { WorkflowDemoMedia, workflowDemos } from './WorkflowDemos';
import { useCommercialOffer } from '../lib/useCommercialOffer';
import './WorkflowLanding.css';
import GrowthNav from './GrowthNav';
import ConstructionExample from './ConstructionExample';
import { constructionExampleForPath } from '../lib/constructionExamples';
import MacMeasurementEvaluation from './MacMeasurementEvaluation';
import WorkflowAnswer from './WorkflowAnswer';
import TakeoffEvaluation from './TakeoffEvaluation';
import MarkupEvaluation from './MarkupEvaluation';
import ComparisonEvaluation from './ComparisonEvaluation';
import SymbolCountEvaluation from './SymbolCountEvaluation';

export const CURRENT_INTERFACE_LABEL = 'PolyPDF for Mac and Windows';

const WorkflowLanding = ({ page, children }) => {
  const offer = useCommercialOffer();
  const demoId = page.demoId || {
    '/pdf-takeoff-software': 'takeoff',
    '/measure-pdf-on-mac': 'takeoff',
    '/construction-pdf-markup': 'review',
    '/compare-pdf-drawings': 'compare'
  }[page.path];
  const demo = workflowDemos.find((item) => item.id === demoId);

  useEffect(() => {
    captureAttribution();
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [page.path]);

  const trackCta = (action) => {
    trackEvent(`landing_${action}`, {
      source: page.source,
      route: page.path
    });
  };

  return (
    <div className="workflow-landing">
      <GrowthNav />

      <main id="workflow-main">
        <section className="workflow-hero">
          <div className="container workflow-hero-grid">
            <div className="workflow-hero-copy">
              <p className="workflow-eyebrow">{page.eyebrow}</p>
              <h1>{page.title}</h1>
              <p className="workflow-lede">{page.lede}</p>
              <p className="workflow-qualifier">{page.qualifier}</p>
              <div className="workflow-cta-row">
                <DownloadCTA
                  source={`${page.source}_hero`}
                  size="large"
                  evaluationHref={page.path === '/pdf-takeoff-software' ? '#try-a-takeoff' : page.path === '/measure-pdf-on-mac' ? '#try-mac-measurement' : undefined}
                  onDownload={() => trackCta('download_click')}
                />
                <DirectCheckoutLink
                  offer={offer}
                  className="secondary-btn workflow-buy-cta"
                  source={`${page.source}_hero`}
                  pageVariant="workflow_hero"
                  onClick={() => trackCta('buy_click')}
                >
                  <FaInfinity /> Unlock PolyPDF Pro · {commercialOffer.price}
                </DirectCheckoutLink>
              </div>
              {page.path === '/measure-pdf-on-mac' && <nav className="takeoff-jump-links" aria-label="Mac measurement decisions"><a href="#preview-and-scale">Preview and drawing scale</a><a href="#construction-example-takeoff">Try the Mac measurement sample</a></nav>}
              {page.path === '/pdf-takeoff-software' && <nav className="takeoff-jump-links" aria-label="Takeoff decisions"><a href="#free-vs-pro">Compare Free and Pro</a><a href="#construction-example-takeoff">Try the practice takeoff</a></nav>}
              {page.path === '/construction-pdf-markup' && <nav className="takeoff-jump-links" aria-label="Markup decisions"><a href="#markup-free-vs-pro">Compare Free and Pro</a><a href="#construction-example-review">Check the review handoff</a></nav>}
              {page.path === '/visual-search-pdf-count' && <nav className="takeoff-jump-links" aria-label="Symbol counting decisions"><a href="#counting-options">Choose Free or Pro for counting</a><a href="#construction-example-electrical">Check the fixture-count exercise</a></nav>}
              {page.path === '/electrical-takeoff-software' && <nav className="takeoff-jump-links" aria-label="Electrical takeoff decisions"><a href="#electrical-options">Choose Free or Pro</a><a href="#electrical-count-check">Check fixtures and receptacles</a></nav>}
              {page.path === '/compare-pdf-drawings' && <nav className="takeoff-jump-links" aria-label="Comparison decisions"><a href="#comparison-options">Compare the revision workflows</a><a href="#construction-example-compare">Try the drawing pair</a></nav>}
            </div>

            <figure className="workflow-hero-visual">
              <div className="workflow-capture-label">
                <span aria-hidden="true" /> {CURRENT_INTERFACE_LABEL}
              </div>
              {demo ? <WorkflowDemoMedia key={demo.id} demo={demo} source={page.source} eager /> : <img
                src={page.image}
                srcSet={page.imageSrcSet}
                sizes={page.imageSizes}
                alt={page.imageAlt}
                width={page.imageWidth || 2280}
                height={page.imageHeight || 1515}
                loading="eager"
                fetchPriority="high"
              />}
              <figcaption>{page.audience}</figcaption>
            </figure>
          </div>

          <div className="container workflow-proof-strip" aria-label="Product facts">
            {page.proofPoints.map(([label, value]) => (
              <div key={label} className="workflow-proof-point">
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
            ))}
          </div>
        </section>

        <WorkflowAnswer path={page.path} />

        <section className="workflow-context">
          <div className="container workflow-context-grid">
            <p className="workflow-section-index">01 / WHY</p>
            <div>
              <h2>{page.problemTitle}</h2>
              <p>{page.problemCopy}</p>
            </div>
          </div>
        </section>

        {!children && !['/pdf-takeoff-software', '/measure-pdf-on-mac'].includes(page.path) && constructionExampleForPath[page.path] && <ConstructionExample kind={constructionExampleForPath[page.path]} context="workflow" />}
        {children}
        {!children && page.path === '/pdf-takeoff-software' && <TakeoffEvaluation />}
        {!children && page.path === '/construction-pdf-markup' && <MarkupEvaluation />}
        {!children && page.path === '/compare-pdf-drawings' && <ComparisonEvaluation />}
        {!children && page.path === '/measure-pdf-on-mac' && <MacMeasurementEvaluation />}
        {!children && page.path === '/visual-search-pdf-count' && <SymbolCountEvaluation />}

        <section className="workflow-steps" id="how-it-works">
          <div className="container">
            <div className="workflow-section-heading">
              <p className="workflow-section-index">02 / WORKFLOW</p>
              <h2>From source PDF to a result you can review.</h2>
            </div>
            <ol className="workflow-step-list">
              {page.workflow.map(([title, description], index) => (
                <motion.li
                  key={title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ delay: index * 0.06 }}
                >
                  <span className="workflow-step-number">0{index + 1}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </motion.li>
              ))}
            </ol>
          </div>
        </section>

        <section className="workflow-outcomes">
          <div className="container workflow-outcomes-grid">
            <div>
              <p className="workflow-section-index">03 / FIT</p>
              <h2>{page.outcomeTitle}</h2>
            </div>
            <ul>
              {page.outcomes.map((outcome) => (
                <li key={outcome}><FaCheck aria-hidden="true" /> <span>{outcome}</span></li>
              ))}
            </ul>
          </div>
        </section>

        <section className="workflow-offer" id="pricing">
          <div className="container workflow-offer-grid">
            <div className="workflow-offer-price">
              <span>PolyPDF Pro</span>
              <OfferPrice compact />
              <small>one time · no subscription</small>
            </div>
            <div className="workflow-offer-copy">
              <h2>Start with the free app. Pay once to unlock every Pro workflow.</h2>
              <p>{licenseRightsText}</p>
              <OfferGuarantee compact />
            </div>
            <div className="workflow-offer-actions">
              <DirectCheckoutLink
                offer={offer}
                className="primary-btn large"
                source={`${page.source}_offer`}
                pageVariant="workflow_offer"
                onClick={() => trackCta('buy_click')}
              >
                Buy the Pro license <FaArrowRight />
              </DirectCheckoutLink>
              <a
                href="#workflow-main"
                className="workflow-text-link"
                onClick={(event) => {
                  event.preventDefault();
                  document.querySelector('.dl-cta a')?.focus();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                <HiOutlineCloudDownload /> Download free first
              </a>
            </div>
          </div>
        </section>

        <section className="workflow-faq">
          <div className="container workflow-faq-grid">
            <div>
              <p className="workflow-section-index">04 / FAQ</p>
              <h2>Questions before you test it.</h2>
            </div>
            <div className="workflow-faq-list">
              {page.faq.map(([question, answer]) => (
                <details key={question}>
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="workflow-related" aria-label="Related PolyPDF workflows">
          <div className="container">
            <p className="workflow-section-index">RELATED GUIDES &amp; WORKFLOWS</p>
            <div className="workflow-related-links">
              {page.related.map(([path, label]) => (
                <Link key={path} to={canonicalPagePath(path)}>{label} <FaArrowRight /></Link>
              ))}
            </div>
          </div>
        </section>
      </main>

    </div>
  );
};

export default WorkflowLanding;
